import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { mediaSrc, avatarSrc, formatApiError, takePrefetched } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdBanner from "../components/AdBanner";
import CommentThread from "../components/CommentThread";
import MediaCard from "../components/MediaCard";
import MediaCarousel from "../components/MediaCarousel";
import { Heart, MessageCircle, Eye, Trash2, Send, Loader2, ArrowLeft, Pencil, Check, X, Lock, Sparkles, EyeOff, Globe, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { MentionInput } from "../components/MentionInput";
import { MentionText } from "../components/MentionText";
import { PeoplePicker } from "../components/PeoplePicker";
import { TaggedPeople } from "../components/TaggedPeople";
import { NotesModal, PostMenu, ReblogButton } from "../components/PostExtras";

export default function MediaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [media, setMedia] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState("");
  const [eDesc, setEDesc] = useState("");
  const [eTags, setETags] = useState("");
  const [ePeople, setEPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [togglingVis, setTogglingVis] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    const pre = takePrefetched(id);
    (pre || api.get(`/media/${id}`).then((r) => r.data)).then((data) => {
      if (data) setMedia(data);
      else return api.get(`/media/${id}`).then((r) => setMedia(r.data));
    }).catch(() => nav("/"));
    api.get(`/media/${id}/related`, { params: { limit: 12 } }).then(({ data }) => setRelated(data)).catch(() => setRelated([]));
    window.scrollTo(0, 0);
  }, [id, nav]);

  const like = async () => {
    if (!user) return nav("/login");
    const { data } = await api.post(`/media/${id}/like`);
    setMedia((m) => ({ ...m, liked: data.liked, likes_count: data.likes_count }));
  };

  const del = async () => {
    if (!window.confirm("Delete this media permanently?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Deleted");
      nav("/explore");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeMe = async () => {
    try {
      const { data } = await api.delete(`/media/${id}/people/me`);
      setMedia((m) => ({ ...m, tagged_users: data.tagged_users }));
      toast.success("You've been removed from this post's tags");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = () => {
    setETitle(media.title);
    setEDesc(media.description || "");
    setETags((media.tags || []).join(", "));
    setEPeople(media.tagged_users || []);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data: ppl } = await api.put(`/media/${id}/people`, { usernames: ePeople.map((p) => p.username) });
      const { data } = await api.put(`/media/${id}`, { title: eTitle, description: eDesc, tags: eTags });
      setMedia((m) => ({ ...m, title: data.title, description: data.description, tags: data.tags, tagged_users: ppl.tagged_users }));
      setEditing(false);
      toast.success("Updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const describe = async () => {
    setDescribing(true);
    try {
      const { data } = await api.post(`/media/${id}/describe`);
      setMedia((m) => ({ ...m, description: data.description }));
      toast.success("AI wrote a fresh description");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't generate a description");
    } finally {
      setDescribing(false);
    }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await api.post(`/media/${id}/reanalyze`);
      toast.success("Re-analyzing… new tags will appear shortly.");
      const before = JSON.stringify(media.tags || []);
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/media/${id}`);
          if (JSON.stringify(data.tags || []) !== before || tries >= 8) {
            setMedia((m) => ({ ...m, title: data.title || m.title, tags: data.tags, category: data.category, ai_caption: data.ai_caption }));
            clearInterval(poll);
            setReanalyzing(false);
            if (tries < 8) toast.success("AI tags updated");
            else setReanalyzing(false);
          }
        } catch {
          if (tries >= 8) { clearInterval(poll); setReanalyzing(false); }
        }
      }, 4000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not re-analyze");
      setReanalyzing(false);
    }
  };

  const setCover = async (index) => {
    try {
      await api.post(`/media/${id}/cover`, { index });
      setMedia((m) => ({ ...m, cover_index: index }));
      toast.success("Album cover updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't set cover");
    }
  };

  const [upscaling, setUpscaling] = useState(false);
  const upscale = async () => {
    setUpscaling(true);
    try {
      const { data } = await api.post(`/media/${id}/upscale`);
      setMedia(data);
      toast.success(data.upscale_result?.changed ? "Upscaled to higher resolution" : "Already at full resolution");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't upscale");
    } finally {
      setUpscaling(false);
    }
  };


  const toggleVisibility = async () => {
    const next = !media.is_private;
    setTogglingVis(true);
    try {
      await api.post(`/media/${id}/visibility`, { is_private: next });
      setMedia((m) => ({ ...m, is_private: next }));
      toast.success(next ? "Moved back to private drafts" : "Published — it's now public");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update visibility");
    } finally {
      setTogglingVis(false);
    }
  };

  if (!media) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#0066FF]" size={32} /></div>;

  const isStaff = user && (user.role === "admin" || user.role === "moderator");
  const isOwner = user && user.id === media.author_id;
  const canEdit = isOwner || isStaff;
  const seoDesc = media.ai_caption || media.description || `${media.title} — ${(media.tags || []).join(", ")}`;
  const altText = media.ai_caption || media.title;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-3 lg:pt-20 pb-16">
      <title>{`${media.title} · KinkVault`}</title>
      <meta name="description" content={seoDesc?.slice(0, 160)} />
      <meta property="og:title" content={media.title} />
      <meta property="og:description" content={seoDesc?.slice(0, 160)} />
      {media.thumb_url && <meta property="og:image" content={mediaSrc(media.thumb_url)} />}
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white font-mono text-xs uppercase tracking-[0.2em] mb-3 lg:mb-5 transition-colors group" data-testid="back-btn">
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" /> Back to the archive
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] gap-6 items-start [&>*]:min-w-0">
        <div className="lg:sticky lg:top-20 rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,1)] flex items-center justify-center min-h-[300px] relative w-full">
          <div className="pointer-events-none absolute inset-0 z-[5] opacity-70" style={{ boxShadow: "inset 0 0 120px 20px rgba(0,0,0,0.65)" }} />
          {media.is_private && canEdit && (
            <div data-testid="private-banner" className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 bg-[#0066FF]/90 backdrop-blur-md text-white px-4 py-2 font-mono text-[11px] uppercase tracking-wider">
              <Lock size={13} /> Private draft — only you can see this
            </div>
          )}
          {media.is_set && media.assets?.length > 1 ? (
            <div className="w-full p-2" data-testid="detail-set">
              <MediaCarousel
                assets={media.assets}
                testid="detail-carousel"
                heightClass={media.assets.length > 1 ? "media-fit-detail-strip" : "media-fit-detail"}
                coverIndex={media.cover_index ?? 0}
                onSetCover={canEdit ? setCover : null}
              />
            </div>
          ) : media.media_type === "video" ? (
            <video data-testid="detail-video" src={mediaSrc(media.url)} controls autoPlay loop playsInline className="w-full media-fit-detail object-contain" />
          ) : (
            <img data-testid="detail-image" src={mediaSrc(media.url)} alt={altText} className="w-full media-fit-detail object-contain" />
          )}
        </div>

        <div>
          {editing ? (
            <div className="space-y-3 mb-4" data-testid="edit-media-form">
              <input data-testid="edit-title-input" value={eTitle} onChange={(e) => setETitle(e.target.value)} placeholder="Title" className="w-full bg-[#141414] border border-[#1C1C1F] text-white font-heading uppercase text-xl px-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl" />
              <MentionInput as="textarea" data-testid="edit-desc-input" value={eDesc} onChange={setEDesc} placeholder="Description" rows={3} className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl resize-none" />
              <PeoplePicker value={ePeople} onChange={setEPeople} testid="edit-people" />
              <input data-testid="edit-tags-input" value={eTags} onChange={(e) => setETags(e.target.value)} placeholder="Tags, comma separated" className="w-full bg-[#141414] border border-[#1C1C1F] text-white font-mono text-sm px-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl" />
              <div className="flex gap-2">
                <button data-testid="edit-save-btn" onClick={saveEdit} disabled={saving} className="flex items-center gap-2 bg-[#0066FF] text-white font-heading uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-[#0055CC] transition-colors disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button>
                <button data-testid="edit-cancel-btn" onClick={() => setEditing(false)} className="flex items-center gap-2 border border-[#1C1C1F] text-[#A1A1AA] font-heading uppercase tracking-widest text-xs px-4 py-2.5 hover:text-white transition-colors"><X size={14} /> Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
              <h1 data-testid="detail-title" className="font-heading text-2xl sm:text-4xl uppercase tracking-tight text-white leading-tight min-w-0 break-words [overflow-wrap:anywhere]">{media.title}</h1>
              {canEdit && (
                <button data-testid="edit-media-btn" onClick={startEdit} className="shrink-0 text-[#52525B] hover:text-[#0066FF] transition-colors p-1.5 border border-[#1C1C1F] hover:border-[#0066FF]"><Pencil size={15} /></button>
              )}
            </div>
          )}

          <Link to={`/u/${media.author_username}`} className="flex items-center gap-3 mb-4 group bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-3">
            {media.author_avatar ? (
              <img src={avatarSrc(media.author_avatar)} alt={media.author_username} className="w-11 h-11 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0066FF] to-[#0047B3] flex items-center justify-center font-heading text-white">{media.author_username?.[0]?.toUpperCase()}</div>
            )}
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold group-hover:text-[#0066FF] transition-colors flex items-center gap-1 min-w-0"><span className="truncate">{media.author_display_name || media.author_username}</span><VerifiedBadge show={media.author_verified} testId="media-author-verified" /></p>
              <p className="font-mono text-[10px] text-[#52525B]">@{media.author_username}</p>
            </div>
            <span className="ml-auto text-[#52525B] group-hover:text-[#00E5FF] font-mono text-[10px] uppercase tracking-wider transition-colors">View →</span>
          </Link>

          {!editing && media.tagged_users?.length > 0 && (
            <div className="mb-4"><TaggedPeople people={media.tagged_users} max={8} testid="detail-tagged" meId={user?.id} onRemoveMe={removeMe} /></div>
          )}
          {!editing && media.description && <p data-testid="detail-description" className="text-[#A1A1AA] text-sm mb-4 whitespace-pre-wrap"><MentionText text={media.description} /></p>}
          {!editing && !media.description && media.ai_caption && (
            <p data-testid="ai-caption" className="text-[#A1A1AA] text-sm mb-4 italic flex items-start gap-1.5"><Sparkles size={13} className="text-[#7c3aed] mt-0.5 shrink-0" /> {media.ai_caption}</p>
          )}

          {!editing && (
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8 bg-[#00E5FF]/70" />
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase">Semantic Index</span>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
              {media.category && (
                <span data-testid="detail-category" className="rounded-full border border-[#a78bfa]/40 bg-[#a78bfa]/10 px-3 py-1 font-mono text-[10px] text-[#a78bfa] uppercase tracking-wider">{media.category}</span>
              )}
              {media.tags.map((t) => (
                <Link key={t} to={`/explore?tag=${t}`} data-testid={`detail-tag-${t}`} className="rounded-full border border-white/[0.06] bg-white/[0.04] px-3 py-1 font-mono text-[10px] text-[#A1A1AA] hover:text-[#00E5FF] hover:border-[#00E5FF]/40 transition-colors uppercase tracking-wider">#{t}</Link>
              ))}
              {canEdit && (
                <button data-testid="reanalyze-btn" onClick={reanalyze} disabled={reanalyzing} title="Re-run AI auto-tagging on this media" className="flex items-center gap-1.5 rounded-full border border-[#a78bfa]/40 px-3 py-1 font-mono text-[10px] text-[#a78bfa] hover:bg-[#7c3aed] hover:text-white transition-colors uppercase tracking-wider disabled:opacity-50">
                  {reanalyzing ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {reanalyzing ? "Analyzing…" : "Re-run AI tags"}
                </button>
              )}
              {canEdit && (
                <button data-testid="describe-btn" onClick={describe} disabled={describing} title="Let AI write a rich description for this post" className="flex items-center gap-1.5 rounded-full border border-[#00E5FF]/40 px-3 py-1 font-mono text-[10px] text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black transition-colors uppercase tracking-wider disabled:opacity-50">
                  {describing ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {describing ? "Writing…" : "AI describe"}
                </button>
              )}
              {canEdit && media.media_type === "image" && (
                <button data-testid="upscale-btn" onClick={upscale} disabled={upscaling} title="Upscale this photo to a higher resolution (replaces in place)" className="flex items-center gap-1.5 rounded-full border border-[#31A24C]/40 px-3 py-1 font-mono text-[10px] text-[#31A24C] hover:bg-[#31A24C] hover:text-black transition-colors uppercase tracking-wider disabled:opacity-50">
                  {upscaling ? <Loader2 size={11} className="animate-spin" /> : <Maximize2 size={11} />} {upscaling ? "Upscaling…" : "Upscale"}
                </button>
              )}
            </div>
            </div>
          )}

          <div className="sticky top-20 z-20 flex items-center gap-3 mb-6 bg-[#0A0A0A]/85 backdrop-blur-xl border border-white/[0.06] rounded-2xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.9)]">
            <button data-testid="like-btn" onClick={like} className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-heading uppercase tracking-widest text-xs transition-all border ${media.liked ? "bg-[#0066FF] border-[#0066FF] text-white shadow-[0_4px_14px_rgba(0,102,255,0.4)]" : "border-white/10 text-white hover:border-[#0066FF] hover:-translate-y-0.5"}`}>
              <Heart size={15} fill={media.liked ? "white" : "none"} /> {media.likes_count}
            </button>
            <ReblogButton item={media} testid="reblog-btn" onChange={(c) => setMedia((m) => ({ ...m, ...c }))}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 hover:border-[#00E676] text-xs font-heading uppercase tracking-widest transition-colors" />
            <button data-testid="notes-btn" onClick={() => setShowNotes(true)} className="text-[#A1A1AA] hover:text-white font-mono text-xs">{(media.likes_count || 0) + (media.reblogs_count || 0)} notes</button>
            <span className="flex items-center gap-1.5 text-[#A1A1AA] font-mono text-xs"><MessageCircle size={14} /> {media.comments_count}</span>
            <span className="flex items-center gap-1.5 text-[#A1A1AA] font-mono text-xs"><Eye size={14} /> {media.views}</span>
            {canEdit && (
              <button data-testid="visibility-toggle-btn" onClick={toggleVisibility} disabled={togglingVis} className={`ml-auto flex items-center gap-2 px-4 py-2.5 rounded-full font-heading uppercase tracking-widest text-xs transition-colors border disabled:opacity-50 ${media.is_private ? "bg-[#0066FF] border-[#0066FF] text-white hover:bg-[#0055CC]" : "border-white/10 text-white hover:border-[#0066FF]"}`}>
                {togglingVis ? <Loader2 size={14} className="animate-spin" /> : media.is_private ? <Globe size={14} /> : <EyeOff size={14} />}
                {media.is_private ? "Publish" : "Make private"}
              </button>
            )}
            {canEdit && (
              <button data-testid="delete-btn" onClick={del} className={`${media.is_private ? "" : "ml-auto "}text-[#52525B] hover:text-[#FF2A5F] transition-colors p-2`}><Trash2 size={16} /></button>
            )}
            {!canEdit && <span className="ml-auto"><PostMenu item={media} testid="media-menu" /></span>}
          </div>
          <NotesModal mediaId={media.id} open={showNotes} onClose={() => setShowNotes(false)} />

          <AdBanner zone="1121861" width={300} height={100} className="mb-6 border-y border-[#1C1C1F]" />

          <CommentThread
            mediaId={id}
            locked={media.comments_locked}
            onCountChange={(d) => setMedia((m) => ({ ...m, comments_count: Math.max(0, (m.comments_count || 0) + d) }))}
          />
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16" data-testid="related-section">
          <div className="flex items-center gap-3 mb-5">
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase">You may also like</span>
            <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
          </div>
          <h2 className="font-heading uppercase text-2xl tracking-tight text-white mb-6">More like this</h2>
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 [column-fill:_balance]">
            {related.map((m) => (
              <div key={m.id} className="mb-3 break-inside-avoid" data-testid={`related-item-${m.id}`}>
                <MediaCard item={m} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
